<script setup lang="ts">
import {computed,onMounted,ref} from 'vue'
import {api,messageOf} from '../api'
import {CheckCircle2,Copy,FileJson,RefreshCw,SearchCode} from 'lucide-vue-next'

type NodeRow={id:string;name:string;state:string;expected_revision_id:string|null;observed_revision_id:string|null;last_seen_at:string|null}
type ConfigPayload={node_id:string;node_name:string;expected_revision_id:string|null;observed_revision_id:string|null;node_state:string;config:any;config_sha256:string|null;snapshot_created_at:string|null;has_config:boolean}

const nodes=ref<NodeRow[]>([]),selectedNodeId=ref(''),payload=ref<ConfigPayload|null>(null),loading=ref(false),error=ref(''),copied=ref(false)
const selectedNode=computed(()=>nodes.value.find(x=>x.id===selectedNodeId.value)||null)
const prettyConfig=computed(()=>payload.value?.config?JSON.stringify(payload.value.config,null,2):'')

async function loadNodes(){
  error.value=''
  try{
    nodes.value=(await api.get('/nodes')).data
    if(!selectedNodeId.value&&nodes.value.length)selectedNodeId.value=nodes.value[0].id
  }catch(e){error.value=messageOf(e)}
}
async function loadConfig(){
  if(!selectedNodeId.value)return
  loading.value=true;error.value='';copied.value=false
  try{payload.value=(await api.get(`/nodes/${selectedNodeId.value}/sing-box-config`)).data}catch(e){error.value=messageOf(e);payload.value=null}finally{loading.value=false}
}
async function refresh(){await loadNodes();await loadConfig()}
async function copyConfig(){
  if(!prettyConfig.value)return
  await navigator.clipboard.writeText(prettyConfig.value)
  copied.value=true
  window.setTimeout(()=>copied.value=false,1600)
}

onMounted(refresh)
</script>

<template>
  <div class="page-head">
    <div>
      <div class="eyebrow">SING-BOX SNAPSHOT</div>
      <h1>完整配置</h1>
      <p>查看后台已发布到节点的完整 sing-box config.json。</p>
    </div>
    <div class="actions">
      <button class="secondary" @click="refresh"><RefreshCw :size="16" :class="{spin:loading}"/>刷新</button>
      <button class="primary" :disabled="!prettyConfig" @click="copyConfig"><Copy :size="16"/>{{copied?'已复制':'复制 JSON'}}</button>
    </div>
  </div>

  <section class="panel config-viewer">
    <header>
      <div>
        <h2>节点配置快照</h2>
        <p>来源：node_snapshots.config_json，按节点 expected revision 读取</p>
      </div>
      <label>
        节点
        <select v-model="selectedNodeId" @change="loadConfig">
          <option v-for="node in nodes" :key="node.id" :value="node.id">{{node.name}} · {{node.state}}</option>
        </select>
      </label>
    </header>

    <div v-if="error" class="empty error"><SearchCode/><b>读取失败</b><span>{{error}}</span></div>
    <div v-else-if="!nodes.length" class="empty"><FileJson/><b>暂无节点</b><span>创建并注册节点后，这里会显示完整配置。</span></div>
    <div v-else-if="payload&&!payload.has_config" class="empty"><FileJson/><b>尚无发布配置</b><span>该节点还没有 expected revision 对应的配置快照。</span></div>
    <div v-else class="config-layout">
      <aside class="config-meta">
        <div><span>节点</span><b>{{payload?.node_name||selectedNode?.name||'—'}}</b></div>
        <div><span>状态</span><b class="status ok" v-if="payload?.node_state==='online'"><CheckCircle2 :size="14"/>online</b><b v-else>{{payload?.node_state||selectedNode?.state||'—'}}</b></div>
        <div><span>Expected Revision</span><code>{{payload?.expected_revision_id||selectedNode?.expected_revision_id||'—'}}</code></div>
        <div><span>Observed Revision</span><code>{{payload?.observed_revision_id||selectedNode?.observed_revision_id||'—'}}</code></div>
        <div><span>Config SHA256</span><code>{{payload?.config_sha256||'—'}}</code></div>
        <div><span>快照时间</span><b>{{payload?.snapshot_created_at||'—'}}</b></div>
      </aside>
      <pre class="config-json">{{loading?'加载中...':prettyConfig}}</pre>
    </div>
  </section>
</template>
